import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'

import type { SwitchboardApi } from '../api/switchboardApi'
import type {
  ApiError,
  CreateSwitchboardRequest,
  ListSwitchboardsParams,
  ListSwitchboardsResponse,
  Pbx,
  Switchboard,
  UpdatePbxRequest,
  UpdateSwitchboardRequest,
} from '../api/types'

interface UseListSwitchboardsParams {
  api: SwitchboardApi
  pbxId: number
  queryParams?: Omit<ListSwitchboardsParams, 'pbx_id'>
  options?: Omit<
    UseQueryOptions<ListSwitchboardsResponse, ApiError>,
    'queryKey' | 'queryFn'
  >
}

/**
 * Hook to list all switchboards for a PBX
 * @param api - Switchboard API instance
 * @param pbxId - Required PBX ID
 * @param queryParams - Optional pagination and ordering
 * @param options - React Query options
 */
export const useListSwitchboards = ({
  api,
  pbxId,
  queryParams,
  options,
}: UseListSwitchboardsParams) => {
  return useQuery<ListSwitchboardsResponse, ApiError>({
    queryKey: ['switchboards', pbxId, queryParams],
    queryFn: async () => {
      const response = await api.listSwitchboards({
        pbx_id: pbxId,
        ...queryParams,
      })

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data as ListSwitchboardsResponse
    },
    enabled: pbxId > 0, // Only enable if pbxId is valid
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseGetSwitchboardParams {
  api: SwitchboardApi
  extensionDialPlan: number
  options?: Omit<UseQueryOptions<Switchboard, ApiError>, 'queryKey' | 'queryFn'>
}

/**
 * Hook to get a single switchboard by extension_dial_plan
 * @param api - Switchboard API instance
 * @param extensionDialPlan - The extension dial plan ID
 * @param options - React Query options
 */
export const useGetSwitchboard = ({
  api,
  extensionDialPlan,
  options,
}: UseGetSwitchboardParams) => {
  return useQuery<Switchboard, ApiError>({
    queryKey: ['switchboard', extensionDialPlan],
    queryFn: async () => {
      const response = await api.getSwitchboard(extensionDialPlan)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data as Switchboard
    },
    enabled: !!extensionDialPlan,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseCreateSwitchboardParams {
  api: SwitchboardApi
  options?: Omit<
    UseMutationOptions<Switchboard, ApiError, CreateSwitchboardRequest>,
    'mutationFn'
  >
}

/**
 * Hook to create a new switchboard
 * @param api - Switchboard API instance
 * @param options - React Query mutation options
 */
export const useCreateSwitchboard = ({
  api,
  options,
}: UseCreateSwitchboardParams) => {
  const queryClient = useQueryClient()

  return useMutation<Switchboard, ApiError, CreateSwitchboardRequest>({
    mutationFn: async (request) => {
      const response = await api.createSwitchboard(request)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data as Switchboard
    },
    onSuccess: (data) => {
      // Invalidate list queries for the PBX
      queryClient.invalidateQueries({ queryKey: ['switchboards'] })
      // Invalidate the specific switchboard query using the response data
      queryClient.invalidateQueries({
        queryKey: ['switchboard', data.extension_dial_plan],
      })
    },
    ...options,
  })
}

interface UseUpdateSwitchboardParams {
  api: SwitchboardApi
  invalidateOnSuccess?: boolean
  options?: Omit<
    UseMutationOptions<
      Switchboard,
      ApiError,
      UpdateSwitchboardRequest & { extension_dial_plan: number }
    >,
    'mutationFn'
  >
}

/**
 * Hook to update a switchboard
 * @param api - Switchboard API instance
 * @param options - React Query mutation options
 */
export const useUpdateSwitchboard = ({
  api,
  invalidateOnSuccess = true,
  options,
}: UseUpdateSwitchboardParams) => {
  const queryClient = useQueryClient()
  const userOnSuccess = options?.onSuccess

  return useMutation<
    Switchboard,
    ApiError,
    UpdateSwitchboardRequest & { extension_dial_plan: number }
  >({
    mutationFn: async ({ extension_dial_plan, ...data }) => {
      // extension_dial_plan goes in URL path, only json_data in body
      const response = await api.updateSwitchboard(extension_dial_plan, data)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      // Handle case where API returns 200 but no data (some APIs do this)
      if (!response.data) {
        // Return a minimal switchboard object - the query will refetch anyway
        return {
          extension_dial_plan: extension_dial_plan,
          json_data: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as Switchboard
      }

      return response.data as Switchboard
    },
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      if (invalidateOnSuccess) {
        queryClient.invalidateQueries({ queryKey: ['switchboards'] })
        queryClient.invalidateQueries({
          queryKey: ['switchboard', variables.extension_dial_plan],
        })
      }
      userOnSuccess?.(data, variables, onMutateResult, context)
    },
  })
}

interface UseDeleteSwitchboardParams {
  api: SwitchboardApi
  options?: Omit<UseMutationOptions<void, ApiError, number>, 'mutationFn'>
}

/**
 * Hook to delete a switchboard
 * @param api - Switchboard API instance
 * @param options - React Query mutation options
 */
export const useDeleteSwitchboard = ({
  api,
  options,
}: UseDeleteSwitchboardParams) => {
  const queryClient = useQueryClient()

  return useMutation<void, ApiError, number>({
    mutationFn: async (extensionDialPlan) => {
      const response = await api.deleteSwitchboard(extensionDialPlan)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }
    },
    onSuccess: (_, extensionDialPlan) => {
      queryClient.invalidateQueries({ queryKey: ['switchboards'] })
      queryClient.removeQueries({
        queryKey: ['switchboard', extensionDialPlan],
      })
    },
    ...options,
  })
}

interface UseGetPbxParams {
  api: SwitchboardApi
  pbxId: number
  options?: Omit<UseQueryOptions<Pbx, ApiError>, 'queryKey' | 'queryFn'>
}

/**
 * Hook to get a single PBX by ID
 * @param api - Switchboard API instance
 * @param pbxId - The PBX ID
 * @param options - React Query options
 */
export const useGetPbx = ({ api, pbxId, options }: UseGetPbxParams) => {
  return useQuery<Pbx, ApiError>({
    queryKey: ['pbx', pbxId],
    queryFn: async () => {
      const response = await api.getPbx(pbxId)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data as Pbx
    },
    enabled: !!pbxId && pbxId > 0,
    ...options,
    retry: options?.retry ?? false,
  })
}

interface UseUpdatePbxParams {
  api: SwitchboardApi | null
  options?: Omit<
    UseMutationOptions<Pbx, ApiError, UpdatePbxRequest & { pbxId: number }>,
    'mutationFn'
  >
}

/**
 * Hook to update PBX day/night settings
 * @param api - Switchboard API instance
 * @param options - React Query mutation options
 */
export const useUpdatePbx = ({ api, options }: UseUpdatePbxParams) => {
  const queryClient = useQueryClient()

  return useMutation<Pbx, ApiError, UpdatePbxRequest & { pbxId: number }>({
    mutationFn: async ({ pbxId, ...data }) => {
      if (!api) {
        return Promise.reject({
          code: 'missing_api',
          message: 'Switchboard API instance is required to update PBX.',
        } as ApiError)
      }

      const response = await api.updatePbx(pbxId, data)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data as Pbx
    },
    onSuccess: (_, variables) => {
      // Invalidate PBX query to refresh settings
      queryClient.invalidateQueries({
        queryKey: ['pbx', variables.pbxId],
      })
    },
    ...options,
  })
}
